import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {formatMoney} from "../../../../../../core/services/utils/utils";
import {Row} from "reactstrap";
import PartnerDataGrid, {useDataGridSavedState} from "../../../../../containers/data-grid";
import {MonerisTransactionsDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import TransactionReceiptDialog from "../../../../../components/dialogs/transaction-receipt-dialog";
import {DataGridColumnAlignments, DataGridColumnTypes} from "../../../../../../packages/data-grid";
import {ReactComponent as Passed} from "../../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../../assets/images/validations/failed.svg";
import {ReactComponent as RollBackIdentifier} from "../../../../../../assets/images/transaction-identifier-icon.svg";
import AccountingTransactionsSearchSection from "../../../../../components/search-sections/accounting-transactions-search-section";
import AccountingTransactionsChartDialog from "../../../../../components/dialogs/accounting-transactions-chart-dialog";
import Api from "../../../../../../core/services/api/api";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import {Button} from "@mui/material";
import IconButton from "../../../../../components/base/button/icon_button";
import {ReceiptLongRounded} from "@mui/icons-material";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import DateUtils from "../../../../../../core/services/date-utils";

const orderByPropNames = [
    MonerisTransactionsDataGridCellKeys.succeeded,
    MonerisTransactionsDataGridCellKeys.identifier,
    MonerisTransactionsDataGridCellKeys.type,
    MonerisTransactionsDataGridCellKeys.amount,
    MonerisTransactionsDataGridCellKeys.submitDateTime,
    MonerisTransactionsDataGridCellKeys.iso,
    MonerisTransactionsDataGridCellKeys.responseCode,
];

const exportingChunkSize = 50;

const initReceiptDialogState = {title: '', open: false, customerId: null, receiptId: null, close: () => void 0};

const dataGridSaveConfig = {
    name: 'AccountingOnlinePaymentTransactionsViewDataGrid',
    version: 2,
}

const AccountingOnlinePaymentTransactionsView = () => {
    const [loadingStats, setLoadingStats] = useState(true);
    const [data, setData] = useState([]);
    const [stats, setStats] = useState({});
    const [chartDialogOpen, setChartDialogOpen] = useState(false);
    const isMounted = useIsMounted();
    const [receiptDialogState, setReceiptDialogState] = useState(initReceiptDialogState);
    /*** @type {React.MutableRefObject<DataGridController>}*/
    const dataGridRef = useRef();
    const {configureNotebookTags} = useConfigureNotebook('accounting-online-payment-transactions');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.accountingOnlinePaymentTransactions);
        configureBehavioralTool((tags) => tags.accountingOnlinePaymentTransactions);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Fetches the statistics of this view from the server.
     * @return {Promise<void>}
     */
    const getStatistics = useCallback(async () => {
        setLoadingStats(true);
        const response = await Api.getOnlinePaymentReportStatistics();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setStats(response?.data ?? {});
        }
        setLoadingStats(false);
    }, [isMounted])

    /**
     * As soon as the component is mounted:
     * - fetches the statistics of this view from the server.
     */
    useEffect(() => {
        getStatistics().then();
    }, [getStatistics])

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(operatorAccessLevels.packages.sales.paymentReceipts ? [
                {
                    name: MonerisTransactionsDataGridCellKeys.receipt,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    width: 0,
                    resizable: false,
                    visibilityToggleable: false,
                    pinnedToggleable: false,
                    reorderable: false,
                    sortable: false,
                    pinned: true,
                },
            ] : []),
            {
                title: "Succeeded",
                name: MonerisTransactionsDataGridCellKeys.succeeded,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                pinned: true,
                sortable: true,
                pinnedToggleable: true,
                resizable: false,
                visibilityToggleable: true,
            },
            {
                title: "Last Four Digits",
                name: MonerisTransactionsDataGridCellKeys.lastFourDigits,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 150,
            },
            {
                title: "Customer",
                name: MonerisTransactionsDataGridCellKeys.customer,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Identifier",
                name: MonerisTransactionsDataGridCellKeys.identifier,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
            },
            {
                title: "Original Payment",
                name: MonerisTransactionsDataGridCellKeys.originalPayment,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 150,
            },
            {
                title: 'Type',
                name: MonerisTransactionsDataGridCellKeys.type,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: 'Amount',
                name: MonerisTransactionsDataGridCellKeys.amount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
            },
            {
                title: 'Auth Code',
                name: MonerisTransactionsDataGridCellKeys.authCode,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: 'Submit Date & Time',
                name: MonerisTransactionsDataGridCellKeys.submitDateTime,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 150,
            },
            {
                title: 'Provider Submit Date & Time',
                name: MonerisTransactionsDataGridCellKeys.providerSubmitDateTime,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 210,
            },
            {
                title: 'Message',
                name: MonerisTransactionsDataGridCellKeys.message,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: 'Invoice Reference',
                name: MonerisTransactionsDataGridCellKeys.monerisInvoice,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 300,
            },
            {
                title: 'Reference',
                name: MonerisTransactionsDataGridCellKeys.reference,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.number,
                sortable: false,
            },
            {
                title: 'ISO',
                name: MonerisTransactionsDataGridCellKeys.iso,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
            },
            {
                title: 'Response Code',
                name: MonerisTransactionsDataGridCellKeys.responseCode,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 150,
            },

        ], [operatorAccessLevels.packages.sales.paymentReceipts])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Prepares the api call request body based on the filters, orderBy, and pagination
     * @type {(function(UseSearchFunctionArg): any)}
     */
    const prepareApiRequest = useCallback((args) => {
        const res = {
            customerIds: Array.from(new Set(args.customers?.map(e => e.id) ?? []).values()),
            keyword: args.filters?.keyword?.toString(),
            transactionTypeCode: args.filters?.type,
            startDate: args.filters?.startDate,
            endDate: args.filters?.endDate,
            identifier: args.filters?.identifier,
            paginationDs: args.paginationInfo,
            sorts: [],
        }
        if (args.orderBy?.field === MonerisTransactionsDataGridCellKeys.providerSubmitDateTime) {
            args.orderBy.field = MonerisTransactionsDataGridCellKeys.submitDateTime
        }
        if (args.orderBy?.field && orderByPropNames.includes(args.orderBy.field)) {
            res.sorts.push([{
                property: args.orderBy.field,
                isDesc: args.orderBy.descending
            }])
        }
        return res;
    }, [])

    /**
     * Fetches the moneris transactions from server based on filter specifications
     * Updates the pagination info and sets the data in the inner state
     *
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = prepareApiRequest(args);
        const response = await Api.searchOnlinePaymentReports(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.items ?? [])
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response.data?.totalCount ?? 0,
            }))
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [prepareApiRequest, isMounted])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        customers: [],
        filters: {},
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)
    const canExportOnlinePaymentTransactions = operatorAccessLevels.packages.sales.exportOnlinePaymentTransactions;

    /**
     * Handles click on the identifier
     * Sets the identifier in the query or removes it based on the value of the identifier
     * @param {number} identifier
     * @param {boolean} rollback
     */
    const onIdentifierClicked = useCallback((identifier, rollback) => {
        searchData.setFilters?.(e => ({
            ...e,
            identifier: rollback ? undefined : identifier,
        }))
    }, [searchData])

    /**
     * Formats the exported data's entries and returns the info to be exported.
     * @param {any[]} list
     * @return {DataGridExportInfo}
     */
    const formatExportingData = useCallback((list) => {
        return {
            title: `Online payment transactions`,
            body: list?.map(item => ({
                'Succeeded': item.succeeded ?? false,
                'Card Last Four Digits': item?.lastFourDigits ?? '--',
                'Customer Name': item.customer?.name ?? '--',
                'Identifier': item.receiptId ?? '--',
                'Original Payment': item?.originalPaymentReference ?? '--',
                'Type': item?.type ?? '--',
                'Amount': !!item.amount ? formatMoney(`${item.amount}`) : '--',
                'Auth Code': !!item.authCode ? item.authCode?.toString() : '--',
                'Submit Date & Time': item?.rawSubmittedDateTime ? DateUtils.instance(item.rawSubmittedDateTime).local().format(DateUtils.dateTimeFormat) : "--",
                'Provider Submit Date & Time': item?.rawSubmittedDateTime ? DateUtils.instance(item.rawSubmittedDateTime).format(DateUtils.dateTimeFormat) + ` (${DateUtils.instance(item.rawSubmittedDateTime).offsetName()})` : '--',
                'Message': item.terminalMessage ?? '--',
                'Payment Provider Invoice': item.invoiceReference ?? '--',
                "Reference": item?.referenceNumber ?? '--',
                'ISO': item?.iso?.toString() ?? '--',
                'Response Code': item?.responseCode ?? '--'
            })) ?? [],
        }
    }, [])

    /**
     * Fetches the all transaction items for the credit card
     * Calls the function for generating the csv file
     @returns {Promise<DataGridExportInfo | undefined>}
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.resultList?.length) {
                break;
            }
            entries.push(...res.data.resultList);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData])

    /**
     * Opens the receipt dialog
     * @type {(function(*): void)|*}
     */
    const openReceiptDialog = useCallback((data) => {
        setReceiptDialogState({
            title: 'Transaction Receipt',
            open: true,
            customerId: data.customer?.id,
            receiptId: data.receiptId,
            close: () => setReceiptDialogState(initReceiptDialogState)
        });
    }, [])

    /**
     * Memo version of table row records used in data grid
     */
    const rows = useMemo(() => {
        return data?.map(item => ({
            key: item.receiptId,
            data: item,
            cells: {
                [MonerisTransactionsDataGridCellKeys.receipt]:
                    <IconButton
                        color={'paper.primary'}
                        onPressed={() => openReceiptDialog(item)}
                    >
                        <ReceiptLongRounded className={'icon normal'}/>
                    </IconButton>,
                [MonerisTransactionsDataGridCellKeys.succeeded]: item.succeeded
                    ? <Passed/>
                    : <Failed/>,
                [MonerisTransactionsDataGridCellKeys.lastFourDigits]: item.lastFourDigits,
                [MonerisTransactionsDataGridCellKeys.customer]: item.customer?.name,
                [MonerisTransactionsDataGridCellKeys.identifier]:
                    Number(searchData.filters?.identifier) === item.receiptId
                        ? <div
                            className={'identifier-roll-back-button'}
                            onClick={() => onIdentifierClicked(item.receiptId, true)}
                        >
                            <RollBackIdentifier/>
                            <p>{item.receiptId}</p>
                        </div>
                        : <div
                            className={'identifier-text-button'}
                            onClick={() => onIdentifierClicked(item.receiptId, false)}
                        >
                            {item.receiptId}
                        </div>,
                [MonerisTransactionsDataGridCellKeys.originalPayment]:
                    Number(searchData.filters?.identifier) === item.originalPaymentReference
                        ? (
                            <div
                                className={'identifier-roll-back-button'}
                                onClick={() => onIdentifierClicked(item.originalPaymentReference, true)}
                            >
                                <RollBackIdentifier/>
                                <p>{item?.originalPaymentReference ?? "--"}</p>
                            </div>
                        )
                        : (
                            <div
                                className={'identifier-text-button'}
                                onClick={() => onIdentifierClicked(item.originalPaymentReference, false)}
                            >
                                {item.originalPaymentReference}
                            </div>
                        ),
                [MonerisTransactionsDataGridCellKeys.type]: item?.type,
                [MonerisTransactionsDataGridCellKeys.amount]: item?.amount,
                [MonerisTransactionsDataGridCellKeys.authCode]: item?.authCode,
                [MonerisTransactionsDataGridCellKeys.submitDateTime]: item?.rawSubmittedDateTime ? DateUtils.instance(item.rawSubmittedDateTime).local().format(DateUtils.dateTimeFormat) : null,
                [MonerisTransactionsDataGridCellKeys.providerSubmitDateTime]: item?.rawSubmittedDateTime ? DateUtils.instance(item.rawSubmittedDateTime).format(DateUtils.dateTimeFormat) + ` (${DateUtils.instance(item.rawSubmittedDateTime).offsetName()})` : null,
                [MonerisTransactionsDataGridCellKeys.message]: item?.terminalMessage,
                [MonerisTransactionsDataGridCellKeys.monerisInvoice]: item?.invoiceReference,
                [MonerisTransactionsDataGridCellKeys.reference]: item?.referenceNumber,
                [MonerisTransactionsDataGridCellKeys.iso]: item?.iso,
                [MonerisTransactionsDataGridCellKeys.responseCode]: item?.responseCode,
            }
        }))
    }, [data, onIdentifierClicked, openReceiptDialog, searchData.filters?.identifier])

    return (
        <>
            <div className={'moneris-transactions-view'}>
                <Row className='no-gutters w-100 d-flex justify-content-between align-items-start my-4 mt-3'>
                    <h2 className={'text-xl font-weight-semi-bold'}>
                        Online Payment Transactions
                    </h2>
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        onClick={() => setChartDialogOpen(true)}
                    >
                        View Analytics
                    </Button>
                </Row>
                <Row className='no-gutters indicator-wrapper mb-5'>
                    {
                        loadingStats ?
                            <div className={'moneris-transaction-indicators loading'}>
                                <div/>
                                <div/>
                                <div/>
                            </div>
                            : <>
                                <div className={'flex-grow-0'}>
                                    <div className={'indicator'}>
                                        <div className={'indicator-number'}>
                                            <p>{formatMoney(stats?.totalPayment ?? 0)}</p>
                                        </div>
                                        <p className={'indicator-label'}>Total Payment</p>
                                    </div>
                                </div>
                                <div className={'flex-grow-0'}>
                                    <div className={'indicator'}>
                                        <div className={'indicator-number'}>
                                            <p>{formatMoney(stats?.totalRefund ?? 0)}</p>
                                        </div>
                                        <p className={'indicator-label'}>Total Refund</p>
                                    </div>
                                </div>
                                <div className={'flex-grow-0'}>
                                    <div className={'indicator'}>
                                        <div className={'indicator-number'}>
                                            <p>{formatMoney(stats?.currentPreAuth ?? 0)}</p>
                                        </div>
                                        <p className={'indicator-label'}>Current Pre-Auth</p>
                                    </div>
                                </div>
                            </>
                    }
                </Row>
                <AccountingTransactionsSearchSection
                    searchData={searchData}
                />
                <div className={'mb-5'}/>
                <PartnerDataGrid
                    ref={dataGridRef}
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={rows}
                    singleSelectionOnly
                    state={{
                        sortBy: searchData.orderBy,
                        pagination: searchData.paginationInfo,
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                    }}
                    getExportInfo={canExportOnlinePaymentTransactions ? exportToCsv : undefined}
                    onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </div>
            <TransactionReceiptDialog
                title={receiptDialogState.title}
                open={receiptDialogState.open}
                customerId={receiptDialogState.customerId}
                receiptId={receiptDialogState.receiptId}
                setOpen={receiptDialogState.close}
            />
            <AccountingTransactionsChartDialog
                setOpen={setChartDialogOpen}
                open={chartDialogOpen}
            />
        </>
    );
}

export default AccountingOnlinePaymentTransactionsView;
